import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";

import { OfferingDetailsDayPassWithCapacity } from "../template_offering_details_daypass_with_capacity";
import { useMDotAnalytics } from "./mdot_analytics";
import {
  dayOfficeGeneralInfoHE,
  generalInfoEN,
  partnerConfig,
  testimonialsEN,
  testimonialsHE,
} from "./mdot_partner_config";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Text } from "../text";
import { Spacer } from "../spacer";

const Theme = lazyWithRetry(() => import("./mdot_theme"));

export function OfferingMDotDayOffice() {
  useMDotAnalytics();
  const { locale } = useI18n();
  const faqsEN = [
    {
      question: "What are your operating hours?",
      answer:
        "We are open for daily on-demand booking on Sunday-Thursday, 8:30 am to 5:30 pm. We would be happy to have you as our guest during these operating hours.",
    },
    {
      question: "Can I remain after 5:30 pm?",
      answer:
        "Access to daily guests is limited to operating hours only. It is not permitted to remain in the complex beyond these hours.",
    },
    {
      question: "How can I change or cancel a booking?",
      answer:
        "Bookings can be changed or canceled up to 24 hours before the time of the booking (i.e., by 8:30 am on the day before your reservation), at the latest.",
    },
    {
      question: "What does a Daily Office include?",
      answer: `A Daily Office includes access to the complex and common spaces – the cozy lounge, fully accessorized kitchen and lovely garden. In the office itself,
          you will find comfortable workstations, a fast and secure WIFI and wired Internet connection and maximum privacy. The offices do not include screens. Some of the offices have a whiteboard,
          some have a window with a view of the garden. Your office will be assigned to you on the day of arrival, based on availability in the booked category, with an effort made to meet your personal preferences.`,
    },
    {
      question: "What is the difference between the offices?",
      answer: (
        <Fragment>
          <Text>
            We offer three categories of day offices to address the different
            needs:
          </Text>
          <Spacer size={8} />
          <Text weight="700">1. Single Office </Text>
          <Text>
            {" "}
            – A small office designed for one person, which does not allow for
            guests in the office or complex.
          </Text>
          <Spacer size={4} />
          <Text weight="700">2. Executive Office </Text>
          <Text>
            – A spacious and intimate office, ideal for one-on-one meetings. Up
            to two guests may be invited at the same time in the office and/or
            complex.
          </Text>
          <Spacer size={4} />
          <Text weight="700">3. Staff offices </Text>
          <Text>– For up to four or six people. </Text>
          <Spacer size={8} />
          <Text>
            All the offices are available on Sunday-Thursday, 8:30 am to 5:30
            pm.{" "}
          </Text>
        </Fragment>
      ),
    },
    {
      question: `Can I invite guests?`,
      answer: `Only the Executive Office includes guests (up to 2 guests). If you wish to invite additional guests to the complex, you may do so by booking one of our meeting rooms, subject to availability and for a separate fee.`,
    },
    {
      question: `Is the Daily Office shared or is it closed and private?`,
      answer: `The Daily Office is private, with a door, and on the day of the booking it will be at your disposal only.`,
    },
    {
      question: "Where can I park?",
      answer: `What could be better than parking in a reserved spot in the building? For that option,
                contact us by email at info@m-dot.co.il and book the number of parking spaces you need in advance.
                Parking spaces are subject to availability and an additional fee.
                Alternately, free parking is available within a short walking distance,
                in an unpaved parking lot next to the Hermozo banquet hall (entry via HaZriya Street).
              `,
    },
    {
      question: "Where can I eat?",
      answer: `Food can be ordered in from a wide variety of restaurants in Modiin, or you can avail yourself of the dining
                room in the building which offers a delicious kosher lunch for an additional fee. You can always grab a snack from the snack dispenser
                in the lobby, or bring food from home, which you can store in the refrigerator and heat up in the microwave in the main kitchen.
              `,
    },
    {
      question: "Is there a printer available for use?",
      answer:
        "A printer is available for your use in the administration area. It can be used for a separate fee.",
    },
  ];

  const faqsHE = [
    {
      question: "מהן שעות הפעילות שלכם?",
      answer:
        "המתחם פתוח להזמנות יומיות בימים א'-ה' בין השעות 08:30 - 17:30. נשמח לארח אותך אצלנו במסגרת שעות פעילות אלה.",
    },
    {
      question: "האם אפשר להישאר אחרי 17:30?",
      answer:
        "הגישה לאורחים יומיים מוגבלת לשעות הפעילות בלבד. לא ניתן להישאר במתחם מעבר לשעות אלה",
    },
    {
      question: "איך אפשר לשנות או לבטל הזמנה?",
      answer:
        "ניתן לשנות או לבטל הזמנה עד 24 שעות לפני המועד שהוזמן (לכל המאוחר עד לשעה 08:30 בבוקר ביום שלפני מועד ההזמנה).",
    },
    {
      question: "מה כולל משרד יומי?",
      answer: `משרד יומי כולל גישה למתחם ולאזורים הציבוריים  - הלאונג' האינטימי, המטבח המאובזר והגינה המפנקת שלנו. בתוך המשרד עצמו תוכלו ליהנות מעמדות עבודה נוחות, מחיבור קווי ואלחוטי לאינטרנט מהיר ומאובטח ומפרטיות מירבית. המשרדים אינם כוללים מסכים. בחלק מהמשרדים יש לוח מחיק, חלקם עם חלון הפונה לגינה, כאשר המשרד הספציפי יוקצה לכם ביום ההגעה, על פי זמינות ובאותה קטגוריה, תוך ניסיון כן להיענות להעדפותיכם האישיות	`,
    },
    {
      question: "איזה סוגי משרדים יש ומה ההבדל ביניהם?",
      answer: (
        <Fragment>
          <Text>ישנן שלוש קטגוריות של משרדים יומיים, בהתאם לצרכים השונים:</Text>
          <Spacer size={8} />
          <Text weight="700">1. משרד אישי </Text>
          <Text>
            {" "}
            – משרד קטן המיועד לאדם אחד ואינו מאפשר קבלת אורחים במשרד ו/או במתחם.
          </Text>
          <Spacer size={4} />
          <Text weight="700">2. משרד אקזקיוטיב (משרד ל – 2) </Text>
          <Text>
            – משרד אינטימי ומרווח, אידאלי לפגישות "אחד על אחד" . מאפשר לארח עד
            שני אנשים במקביל במשרד ו/או במתחם.
          </Text>
          <Spacer size={4} />
          <Text weight="700">3. משרד צוות </Text>
          <Text>– לארבעה / שישה אנשים</Text>
          <Spacer size={8} />
          <Text>כל המשרדים זמינים בימים א' – ה' בין השעות 08:30 – 17:30 .</Text>
        </Fragment>
      ),
    },
    {
      question: "לכמה אנשים יש גישה למשרד?",
      answer: `מספר האנשים המורשים להיכנס למתחם מטעמכם תלוי בסוג המשרד שהוזמן. משרד אישי מאפשר כניסה למזמין בלבד, ללא אירוח אנשים נוספים. משרד אקזקיוטיב מאפשר כניסה של המזמין עם מקסימום שני אורחים נוספים במקביל, משרד צוות לארבעה אנשים מאפשר כניסה של עד ארבעה אנשים ביום, משרד צוות לשישה אנשים מאפשר גישה לעד שישה אנשים ביום. בכל מקרה החיוב מתבצע לפי סוג החדר ולא ינתן זיכוי ו/או החזר בגין אי הגעה של המזמין או מי מטעמו.	`,
    },
    {
      question: "אפשר להזמין אורחים?",
      answer: `רק משרד אקזקיוטיב כולל אירוח (עד 2 אורחים במקביל). במידה ותרצו להזמין אורחים נוספים למתחם תוכלו לעשות זאת באמצעות הזמנת אחד מחדרי הישיבות שלנו, בכפוף לזמינות ובתשלום נפרד.`,
    },
    {
      question: "האם המשרד היומי הוא משותף או סגור ופרטי?",
      answer:
        "המשרד היומי הוא פרטי, עם דלת, וביום ההזמנה הוא יעמוד לרשותכם בלבד",
    },
    {
      question: `איפה אוכלים?`,
      answer: `ניתן להזמין משלוחים ממגון מסעדות במודיעין או לעלות לחדר האוכל שבבניין המציע בופה צהריים עשיר וכשר (בתשלום נוסף). בנוסף תוכלו לסגור את הפינה עם חטיפים מהמכונה שבלובי או להביא איתכם אוכל מהבית, לאחסן במקרר ולחמם במיקרו שבמטבח הראשי .	`,
    },
    {
      question: `יש מדפסת זמינה במתחם?`,
      answer: `בפינת האדמיניסטרציה במתחם עומדת לרשותכם מכונת צילום / מדפסת. ניתן לעשות בה שימוש בתשלום נפרד	`,
    },
  ];

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsDayPassWithCapacity
        showAddress={false}
        showDirections
        partnerConfig={partnerConfig}
        data={{
          testimonials:
            locale.baseLanguageTag === "en" ? testimonialsEN : testimonialsHE,
          generalInfo:
            locale.baseLanguageTag === "en"
              ? generalInfoEN
              : dayOfficeGeneralInfoHE,
          faqs: locale.baseLanguageTag === "en" ? faqsEN : faqsHE,
        }}
      />
    </Fragment>
  );
}
